import { useState, useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';

export function useDebouncedSearch(
  onSearch: (value: string) => void,
  delay: number = 500,
  initialValue: string = ''
) {
  const [searchValue, setSearchValue] = useState(initialValue);
  const debouncedSearchValue = useDebounce(searchValue, delay);

  // Keep onSearch in a ref so that callers whose identity changes on every
  // render (e.g. a useCallback bound to a changing setSearchParams) don't
  // trigger onSearch to fire when only the callback identity changed.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // Skip firing onSearch on the initial mount — the caller already has the
  // initial value and firing again would rewrite the URL (stripping page, etc.)
  // for no reason and cause a duplicate fetch on every navigation.
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    onSearchRef.current(debouncedSearchValue);
  }, [debouncedSearchValue]);

  return {
    searchValue,
    setSearchValue,
    debouncedSearchValue
  };
}
